/**
 * The ⓘ beside a page title ("About this page"). See {@link usePageInfoDialog}.
 */
import { Info } from "lucide-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { PageInfoId } from "@/lib/i18n/types/pageInfo";
import { cn } from "@/lib/utils";
import { usePageInfoDialog, type PageInfoOptions } from "./usePageInfoDialog";

export interface PageInfoButtonProps extends PageInfoOptions {
  page: PageInfoId;
  className?: string;
}

export function PageInfoButton({ page, className, ...options }: PageInfoButtonProps) {
  const { label, openDialog, dialog, triggerRef } = usePageInfoDialog(page, options);

  return (
    <>
      {/* Own provider: the ⓘ also renders in tests and hosts mounted outside App's. */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              aria-label={label}
              aria-haspopup="dialog"
              onClick={() => openDialog()}
              className={cn(
                "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                className
              )}
            >
              <Info className="h-4 w-4" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {dialog}
    </>
  );
}
